type InsertOrReplaceInput<T> = {
  list: readonly T[];
  newItems: T | ItemAction<T> | readonly T[];
  position: Position<T>;
  replace?: boolean;
};

type InsertOrReplaceRes<T> = {
  list: T[];
  prevItem: T | null;
  success: boolean;
};

type ItemAction<TInput, TReturn = TInput> = (prevItem: TInput) => TReturn;

type Predicate<T> = (item: T) => boolean;

export type Position<T> = Predicate<T> | number;

const isAction = <T>(item: T | ItemAction<T>): item is ItemAction<T> => typeof item === 'function';

const isList = <T>(items: T | ItemAction<T> | readonly T[]): items is readonly T[] =>
  Array.isArray(items);

const getNextItem = <T>(prevItem: T | null, newItem: T | ItemAction<T>) => {
  if (!isAction(newItem)) return newItem;

  return prevItem !== null ? newItem(prevItem) : null;
};

const insertOrReplace = <T>({
  list,
  newItems,
  position,
  replace = false,
}: InsertOrReplaceInput<T>): InsertOrReplaceRes<T> => {
  const defaultRes = {
    list: [...list],
    prevItem: null,
    success: false,
  };
  const deleteCount = Number(replace);

  const index = (() => {
    if (typeof position === 'number') return position;

    return list.findIndex(position);
  })();

  const outOfRange =
    (typeof position !== 'number' && index === -1) ||
    (typeof position === 'number' && !!deleteCount && !list.at(position));

  if (outOfRange) return defaultRes;

  if (isList(newItems))
    return {
      ...defaultRes,
      list: list.toSpliced(index, deleteCount, ...newItems),
      success: true,
    };

  const prevItem = list[index] ?? null;

  const nextItem = getNextItem(prevItem, newItems);

  if (nextItem === null)
    return {
      ...defaultRes,
      prevItem,
    };

  return {
    list: list.toSpliced(index, deleteCount, nextItem),
    prevItem,
    success: true,
  };
};

const insert = <TItem>(
  list: readonly TItem[],
  newItems: TItem | readonly TItem[],
  position: Position<TItem>,
) =>
  insertOrReplace({
    list,
    newItems,
    position,
  }).list;

const replace = <TItem>(
  list: readonly TItem[],
  newItem: TItem | ItemAction<TItem>,
  position: Position<TItem>,
) =>
  insertOrReplace({
    list,
    newItems: newItem,
    position,
    replace: true,
  }).list;

const replaceMulti = <T>(
  list: readonly T[],
  newItems: T | ItemAction<T>,
  predicate: Predicate<T>,
) =>
  list.map((item) => {
    if (!predicate(item)) return item;

    return getNextItem(item, newItems) ?? item;
  });

const append = <TItem>(list: readonly TItem[], newItems: TItem | readonly TItem[]) =>
  isList(newItems) ? [...list, ...newItems] : [...list, newItems];

const replaceOrAppend = <TItem>(
  list: readonly TItem[],
  newItem: TItem | ItemAction<TItem | null, TItem>,
  position: Position<TItem>,
) => {
  const res = insertOrReplace({
    list,
    newItems: newItem,
    position,
    replace: true,
  });

  if (res.success) return res.list;

  const nextItem = isAction(newItem) ? newItem(res.prevItem) : newItem;

  return append(list, nextItem);
};

const remove = <TItem>(list: readonly TItem[], position: Position<TItem>) => {
  if (typeof position !== 'number') return list.filter((item) => !position(item));

  if (!list.at(position)) return [...list];

  return list.toSpliced(position, 1);
};

const listUtils = {
  append,
  insert,
  remove,
  replace,
  replaceMulti,
  replaceOrAppend,
};

export default listUtils;
